import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl, ensureArray, getApiHeaders } from '../config';
import { DEMO_SCOPE, PUBLIC_DEMO_PATH } from './landingContent';
const timeOf = value => {
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : 0;
};
const formatTime = value => timeOf(value) ? new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC'
}).format(new Date(value)) : 'Unavailable';
export default function SignalsPreviewSection() {
  const [signals, setSignals] = useState([]);
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetch(buildApiUrl('/api/bot/ui/mtf/latestSignals', {
      mode: 'PUBLIC',
      ...DEMO_SCOPE
    }), {
      headers: getApiHeaders(),
      cache: 'no-store',
      signal: controller.signal
    }).then(response => {
      if (!response.ok) throw new Error('Preview unavailable');
      return response.json();
    }).then(rows => {
      if (controller.signal.aborted) return;
      const selected = ensureArray(rows).filter(row => row && typeof row === 'object' && (!row.exchange || row.exchange === DEMO_SCOPE.exchange) && (!row.marketType || row.marketType === DEMO_SCOPE.marketType)).sort((a, b) => timeOf(b.calculatedCandleCloseTime) - timeOf(a.calculatedCandleCloseTime)).slice(0, 3);
      setSignals(selected);
      setStatus(selected.length ? 'ready' : 'empty');
    }).catch(() => {
      if (!controller.signal.aborted) {
        setSignals([]);
        setStatus('error');
      }
    });
    return () => controller.abort();
  }, [attempt]);
  return <section className="sf-section sf-container" id="preview" aria-labelledby="sf-preview-title">
    <div className="sf-preview-section-heading"><div><p className="sf-eyebrow">TAKE A LOOK INSIDE</p>
      <h2 id="sf-preview-title">Real signals. Visible context.</h2>
      <p className="sf-muted">A public snapshot from {DEMO_SCOPE.exchange} / {DEMO_SCOPE.marketType}. Explore the dashboard for the full available view.</p>
    </div><Link className="sf-text-link" to={PUBLIC_DEMO_PATH}>Explore the dashboard ↗</Link></div>
    <div className="sf-snapshot">
      <div className="sf-snapshot-toolbar"><span><span className="sf-dot" /> PUBLIC SIGNAL SNAPSHOT</span>
        <button type="button" className="sf-refresh" disabled={status === 'loading'} onClick={() => setAttempt(value => value + 1)}>{status === 'loading' ? 'Refreshing…' : 'Refresh preview'}</button></div>
      <div aria-live="polite">
        {status === 'loading' && <p className="sf-snapshot-message" role="status">Loading the public signal snapshot…</p>}
        {status === 'error' && <div className="sf-snapshot-message" role="status"><strong>The preview is temporarily unavailable.</strong><p>You can retry above or explore the public dashboard. No sample signals are substituted.</p></div>}
        {status === 'empty' && <div className="sf-snapshot-message" role="status"><strong>No public signals are available in this snapshot.</strong><p>Availability depends on configured timeframes and incoming market data. Explore the dashboard to see the product layout.</p></div>}
        {status === 'ready' && <div className="sf-snapshot-scroll"><table><caption className="sf-sr-only">Latest public {DEMO_SCOPE.exchange} signal snapshot</caption><thead><tr><th scope="col">Symbol</th><th scope="col">Timeframe</th><th scope="col">Snapshot price</th><th scope="col">Direction</th><th scope="col">Candle close (UTC)</th></tr></thead><tbody>{signals.map((signal, index) => <tr key={`${signal.symbol}-${signal.timeFrame}-${index}`}><td><strong>{signal.symbol || 'Unavailable'}</strong><small>{DEMO_SCOPE.exchange}</small></td><td>{signal.timeFrame ? `${signal.timeFrame} min` : 'Unavailable'}</td><td>{signal.marketPrice ?? signal.close ?? '—'}</td><td>{signal.side || 'See signal details'}</td><td>{formatTime(signal.calculatedCandleCloseTime)}</td></tr>)}</tbody></table></div>}
      </div>
      <p className="sf-snapshot-caption">Snapshot data is not a streaming quote. Check candle timestamps and freshness in the dashboard before using a signal.</p>
    </div>
  </section>;
}
